const HIDE_FIELD_PREFIX = new Set(["non_field_errors", "detail", "__all__"]);
const MAX_LINES = 8;
const MAX_TOTAL_LENGTH = 800;

const humanizeField = (field: string): string =>
  field
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildPrefix = (path: string[]): string => {
  const visible = path.filter(
    (segment, idx) => !(idx === 0 && HIDE_FIELD_PREFIX.has(segment))
  );
  if (!visible.length) return "";
  return `${visible.map((p) => (p.startsWith("#") ? p : humanizeField(p))).join(" › ")}: `;
};

const flattenErrors = (value: unknown, path: string[] = [], seen: WeakSet<object> = new WeakSet()): string[] => {
  if (value == null) return [];

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    return [`${buildPrefix(path)}${trimmed}`];
  }

  // Numeric / boolean leaves are almost never human error messages — they're
  // usually entity ids sneaking through in the error payload. Skip them so we
  // don't render "Scholarship Application Id: 5" as if it were an error.
  if (typeof value === "number" || typeof value === "boolean") return [];

  if (typeof value !== "object") return [];

  // Cycle guard — hand-constructed error objects can have self-references.
  if (seen.has(value as object)) return [];
  seen.add(value as object);

  if (Array.isArray(value)) {
    return value.flatMap((item, idx) => {
      if (item == null || typeof item === "string") {
        return flattenErrors(item, path, seen);
      }
      if (Array.isArray(item) || (typeof item === "object" && Object.keys(item as object).length > 0)) {
        return flattenErrors(item, [...path, `#${idx + 1}`], seen);
      }
      return [];
    });
  }

  return Object.entries(value as Record<string, unknown>).flatMap(([key, v]) =>
    flattenErrors(v, [...path, key], seen)
  );
};

const cap = (lines: string[]): string => {
  if (!lines.length) return "";
  const kept = lines.slice(0, MAX_LINES);
  const overflow = lines.length - kept.length;
  const suffix = overflow > 0 ? `\n(+ ${overflow} more)` : "";
  const joined = kept.join("\n") + suffix;
  return joined.length > MAX_TOTAL_LENGTH
    ? joined.slice(0, MAX_TOTAL_LENGTH - 1).trimEnd() + "…"
    : joined;
};

const looksLikeHtml = (s: string): boolean => /^\s*<(!doctype|html|head|body)/i.test(s);

export const extractApiErrorMessage = (error: any, fallback: string): string => {
  try {
    return extractInner(error, fallback);
  } catch {
    // Last-resort safety net — never let extractor errors surface as UI crashes.
    return fallback;
  }
};

const extractInner = (error: any, fallback: string): string => {
  const data = error?.response?.data;

  if (data && typeof data === "object") {
    // Prefer field-level errors from data.data / data.errors — they tell the
    // user *what* to fix. The wrapper's top-level `messages` is often a
    // generic marker like "Request declined." that hides the real cause.
    const payload = data.data ?? data.errors;
    if (payload && typeof payload === "object") {
      const capped = cap(flattenErrors(payload));
      if (capped) return capped;
    }

    if (Array.isArray(data.messages)) {
      const strings = data.messages
        .filter((m: unknown) => typeof m === "string")
        .map((m: string) => m.trim())
        .filter(Boolean);
      if (strings.length) return cap(strings);
    } else if (typeof data.messages === "string" && data.messages.trim()) {
      return data.messages.trim();
    }

    if (typeof data.detail === "string" && data.detail.trim()) return data.detail.trim();
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();

    const capped = cap(flattenErrors(data));
    if (capped) return capped;
  }

  if (typeof data === "string" && data.trim() && !looksLikeHtml(data)) {
    return data.trim().slice(0, 300);
  }

  const status = error?.response?.status;
  if (status === 400) return "Invalid request. Please review the form and try again.";
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 404) return "The requested resource was not found.";
  if (status === 429) return "Too many requests. Please slow down and try again shortly.";
  if (status && status >= 500) return `Server error (${status}). Please try again in a moment.`;

  if (error?.code === "ERR_NETWORK" || error?.message === "Network Error") {
    return "Cannot reach the server. Check your internet connection and try again.";
  }

  if (typeof error?.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};
